import React, { useState, useRef } from "react";
import toast from "react-hot-toast";
import { RiEyeCloseLine, RiEyeLine } from "react-icons/ri";
import { useNavigate } from "react-router";
import { AuthFeature } from "../../../domain/auth/authFeature";
import { AppStateFeature } from "../../../domain/appState/appStateFeature";
import { AnalyticsFeature } from "../../../domain/analytics/analyticsFeature";
import { CoreError, CoreErrorCause } from "../../../domain/models/CoreError";
import { Button } from "../../components/Button";
import { TextButton } from "../../components/TextButton";
import { GradientBackground } from "../../components/GradientBackground";
import { useResponsive } from "../../hooks/useResponsive";
import { AnalyticsConstants } from "../../assets/constants/analytics.constants";

type AuthMode = "login" | "signup" | "forgot";
type SubmitSource = "button" | "keyboard";

export const AuthScreen: React.FC = () => {
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const [mode, setMode] = useState<AuthMode>("signup");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const passwordInputRef = useRef<HTMLInputElement>(null);

  const getButtonTitle = (): string => {
    switch (mode) {
      case "login":
        return "LOGIN";
      case "signup":
        return "SIGNUP";
      case "forgot":
        return "FORGOTTEN PASSWORD";
      default:
        return "SUBMIT";
    }
  };

  const validateInputs = (): string | null => {
    if (!email.trim()) {
      switch (mode) {
        case "login":
        case "signup":
          return "Oops, please enter your email and password to " + mode + ".";
        case "forgot":
          return "Oops, please enter your email address.";
      }
    }

    if (!email.includes("@") || !email.split("@")[1]?.includes(".")) {
      return "Oops, that doesn't look like a valid email address.";
    }

    if (mode !== "forgot") {
      if (!password) {
        return "Oops, please enter your email and password to " + mode + ".";
      }
      if (mode === "signup" && password.length < 8) {
        return "Your password needs to be at least 8 characters long.";
      }
    }

    return null;
  };

  const getErrorMessage = (error: unknown): string => {
    if (error instanceof CoreError) {
      switch (error.cause) {
        case CoreErrorCause.InvalidCredential:
          return "Oops, that email or password doesn't look right. Please try again.";
        case CoreErrorCause.EmailAlreadyInUse:
          return "This email is already registered. Would you like to sign in instead?";
        case CoreErrorCause.WeakPassword:
          return "Your password needs to be at least 8 characters long.";
        case CoreErrorCause.NetworkConnection:
        case CoreErrorCause.NetworkError:
          return "Oops, we're having trouble connecting. Please check your internet and try again.";
        case CoreErrorCause.RequireRecentAuthentication:
          return "Please sign in again to continue.";
        default:
          return (
            error.context ?? "Oops, something went wrong. Please try again."
          );
      }
    } else if (error instanceof Error) {
      return error.message;
    }
    return "Oops, something went wrong. Please try again.";
  };

  const handleSubmit = async (submitSource: SubmitSource = "button") => {
    const validationError = validateInputs();
    if (validationError) {
      AnalyticsFeature.trackEvent(AnalyticsConstants.events.authSubmit, {
        [AnalyticsConstants.params.mode]: mode,
        [AnalyticsConstants.params.submitSource]: submitSource,
        [AnalyticsConstants.params.result]:
          AnalyticsConstants.values.results.validationError,
      });
      toast.error(validationError);
      return;
    }

    setIsLoading(true);
    try {
      switch (mode) {
        case "login":
          await AuthFeature.login(email.toLowerCase(), password);
          // Navigation handled reactively by AppStateProvider
          break;
        case "signup":
          await AuthFeature.signup(email.toLowerCase(), password);
          AppStateFeature.getInstance().refreshOnboardingState();
          // Navigation handled reactively by AppStateProvider
          break;
        case "forgot":
          await AuthFeature.resetPassword(email.toLowerCase());
          toast.success("Check your email for password reset instructions.");
          setTimeout(() => setMode("login"), 500);
          setIsLoading(false);
          break;
      }
      AnalyticsFeature.trackEvent(AnalyticsConstants.events.authSubmit, {
        [AnalyticsConstants.params.mode]: mode,
        [AnalyticsConstants.params.submitSource]: submitSource,
        [AnalyticsConstants.params.result]:
          AnalyticsConstants.values.results.success,
      });
    } catch (error) {
      AnalyticsFeature.trackEvent(AnalyticsConstants.events.authSubmit, {
        [AnalyticsConstants.params.mode]: mode,
        [AnalyticsConstants.params.submitSource]: submitSource,
        [AnalyticsConstants.params.result]:
          AnalyticsConstants.values.results.error,
        [AnalyticsConstants.params.errorCause]:
          AnalyticsFeature.getErrorCause(error),
      });
      toast.error(getErrorMessage(error));
      setIsLoading(false);
    }
  };

  const handleForgotPasswordOpened = () => {
    AnalyticsFeature.trackEvent(
      AnalyticsConstants.events.authForgotPasswordOpened,
      {
        [AnalyticsConstants.params.fromMode]:
          AnalyticsConstants.values.modes.login,
      },
    );
    setMode("forgot");
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSubmit("keyboard").catch(() => undefined);
    }
  };

  const handleOpenTermsOfUse = () => {
    navigate("/app/legal/terms-of-use");
  };

  const handleOpenPrivacyPolicy = () => {
    navigate("/app/legal/privacy-policy");
  };

  const handleOpenExternal = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const formContent = (
    <div className="w-full max-w-md">
      {/* Email Input */}
      <div className="mb-0 px-15">
        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && mode !== "forgot") {
              passwordInputRef.current?.focus();
            } else if (e.key === "Enter") {
              handleSubmit("keyboard").catch(() => undefined);
            }
          }}
          disabled={isLoading}
          autoCapitalize="none"
          autoCorrect="off"
          className="w-full border-none bg-transparent font-oswald text-[17px] font-normal text-white placeholder-white/60 outline-none"
        />
        <div className="mt-[15px] h-0.5 bg-white" />
      </div>

      {/* Password Input */}
      {mode !== "forgot" && (
        <div className="mt-10 px-15">
          <div className="relative">
            <input
              ref={passwordInputRef}
              type={isPasswordVisible ? "text" : "password"}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isLoading}
              autoCapitalize="none"
              autoCorrect="off"
              className="w-full border-none bg-transparent pr-8 font-oswald text-[17px] font-normal text-white placeholder-white/60 outline-none"
            />
            <button
              type="button"
              onClick={() => {
                const nextVisibility = !isPasswordVisible;
                setIsPasswordVisible(nextVisibility);
                AnalyticsFeature.trackEvent(
                  AnalyticsConstants.events.authPasswordVisibilityToggled,
                  {
                    [AnalyticsConstants.params.mode]: mode,
                    [AnalyticsConstants.params.nowVisible]: nextVisibility,
                  },
                );
              }}
              disabled={isLoading}
              aria-label={isPasswordVisible ? "Hide password" : "Show password"}
              className="absolute right-0 top-1/2 -translate-y-1/2 text-white/80 transition-colors hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isPasswordVisible ? (
                <RiEyeCloseLine size={20} />
              ) : (
                <RiEyeLine size={20} />
              )}
            </button>
          </div>
          <div className="mt-[15px] h-0.5 bg-white" />
        </div>
      )}

      {/* Action Button */}
      <div className="mt-17.5">
        <Button
          title={getButtonTitle()}
          onPress={() => void handleSubmit("button")}
          loading={isLoading}
          disabled={isLoading}
          className="h-10"
        />
      </div>

      {/* Forgotten Password (login mode) */}
      {mode === "login" && (
        <div className="mt-2.5 flex justify-center">
          <TextButton
            title="Forgotten Password?"
            onPress={handleForgotPasswordOpened}
            disabled={isLoading}
          />
        </div>
      )}

      {/* Bottom Toggle */}
      <div className="mt-8 flex items-center justify-center gap-1">
        {mode === "signup" ? (
          <>
            <span className="font-oswald text-sm text-white/60">
              Already have an account?
            </span>
            <TextButton
              title="Login"
              onPress={() => setMode("login")}
              disabled={isLoading}
            />
          </>
        ) : mode === "login" ? (
          <>
            <span className="font-oswald text-sm text-white/60">
              Don't have an account?
            </span>
            <TextButton
              title="Signup"
              onPress={() => setMode("signup")}
              disabled={isLoading}
            />
          </>
        ) : (
          <>
            <span className="font-oswald text-sm text-white/60">
              Remember your password?
            </span>
            <TextButton
              title="Login"
              onPress={() => setMode("login")}
              disabled={isLoading}
            />
          </>
        )}
      </div>
    </div>
  );

  const footerContent = (
    <div className="mx-auto w-full max-w-2xl border-t border-white/20 pt-4 text-center">
      <div className="flex w-full justify-center">
        <div className="grid w-full max-w-sm grid-cols-2 gap-6 text-left sm:w-fit sm:max-w-none sm:gap-10">
          <div className="space-y-2 px-4">
            <p className="font-oswald text-[12px] uppercase tracking-widest text-white/50">
              Contact & Legal
            </p>
            <button
              type="button"
              onClick={() =>
                handleOpenExternal(
                  "https://community.erikalust.com/en/support/home",
                )
              }
              disabled={isLoading}
              className="block text-left font-oswald text-[12px] text-white/70 transition-opacity hover:opacity-80 disabled:opacity-50"
            >
              Customer Support
            </button>
            <button
              type="button"
              onClick={() => handleOpenExternal("https://cs.segpay.com/")}
              disabled={isLoading}
              className="block text-left font-oswald text-[12px] text-white/70 transition-opacity hover:opacity-80 disabled:opacity-50"
            >
              Segpay Billing Support
            </button>
            <button
              type="button"
              onClick={() =>
                handleOpenExternal("http://forms.gle/HcPy2UMMxtTXY8kq5")
              }
              disabled={isLoading}
              className="block text-left font-oswald text-[12px] text-white/70 transition-opacity hover:opacity-80 disabled:opacity-50"
            >
              Complaints &amp; Content Removal
            </button>
          </div>

          <div className="space-y-2 px-4">
            <p className="font-oswald text-[12px] uppercase tracking-widest text-white/50">
              Policies
            </p>
            <button
              type="button"
              onClick={handleOpenTermsOfUse}
              disabled={isLoading}
              className="block text-left font-oswald text-[12px] text-white/70 transition-opacity hover:opacity-80 disabled:opacity-50"
            >
              Terms of Use
            </button>
            <button
              type="button"
              onClick={handleOpenPrivacyPolicy}
              disabled={isLoading}
              className="block text-left font-oswald text-[12px] text-white/70 transition-opacity hover:opacity-80 disabled:opacity-50"
            >
              Privacy Policy
            </button>
          </div>
        </div>
      </div>

      <div className="mt-4 h-px w-full bg-white/20" />

      <div className="mt-4 flex justify-center">
        <div className="w-full max-w-105 space-y-1">
          <p className="text-center font-oswald text-[11px] text-white/60">
            ©2005 - 2026 Lust Productions SLU
          </p>
          <button
            type="button"
            onClick={() =>
              handleOpenExternal(
                "https://xconfessions.com/legal?tab=compliance",
              )
            }
            disabled={isLoading}
            className="w-full text-center font-oswald text-[11px] text-white/60 underline transition-opacity hover:opacity-80 disabled:opacity-50"
          >
            18 U.S.C. 2257 Record-Keeping Requirements Compliance Statement
          </button>
        </div>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <GradientBackground className="flex min-h-dvh flex-col">
        <div className="sticky top-0 z-20 flex h-12 items-center justify-center bg-xcf-gradient-start px-3">
          <h1 className="font-oswald text-[18px] font-normal tracking-wide text-white">
            XCONFESSIONS
          </h1>
        </div>

        <div className="flex flex-1 items-center justify-center px-4">
          {formContent}
        </div>

        <div className="mt-auto w-full px-4 pb-6">{footerContent}</div>
        <div className="sticky bottom-0 z-20 h-6 w-full bg-xcf-gradient-end" />
      </GradientBackground>
    );
  }

  return (
    <GradientBackground className="flex min-h-dvh flex-col items-center px-4">
      {/* Title */}
      <div className="mb-10 mt-10">
        <h1 className="font-oswald text-lg font-normal tracking-wide text-white">
          XCONFESSIONS
        </h1>
      </div>

      {/* Form */}
      <div className="flex w-full flex-1 items-center justify-center">
        <div className="w-full max-w-md">
          {/* Email Input */}
          <div className="mb-0 px-15">
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && mode !== "forgot") {
                  passwordInputRef.current?.focus();
                } else if (e.key === "Enter") {
                  handleSubmit("keyboard").catch(() => undefined);
                }
              }}
              disabled={isLoading}
              autoCapitalize="none"
              autoCorrect="off"
              className="w-full border-none bg-transparent font-oswald text-[17px] font-normal text-white placeholder-white/60 outline-none"
            />
            <div className="mt-[15px] h-0.5 bg-white" />
          </div>

          {/* Password Input */}
          {mode !== "forgot" && (
            <div className="mt-10 px-15">
              <div className="relative">
                <input
                  ref={passwordInputRef}
                  type={isPasswordVisible ? "text" : "password"}
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={isLoading}
                  autoCapitalize="none"
                  autoCorrect="off"
                  className="w-full border-none bg-transparent pr-8 font-oswald text-[17px] font-normal text-white placeholder-white/60 outline-none"
                />
                <button
                  type="button"
                  onClick={() => {
                    const nextVisibility = !isPasswordVisible;
                    setIsPasswordVisible(nextVisibility);
                    AnalyticsFeature.trackEvent(
                      AnalyticsConstants.events.authPasswordVisibilityToggled,
                      {
                        [AnalyticsConstants.params.mode]: mode,
                        [AnalyticsConstants.params.nowVisible]: nextVisibility,
                      },
                    );
                  }}
                  disabled={isLoading}
                  aria-label={
                    isPasswordVisible ? "Hide password" : "Show password"
                  }
                  className="absolute right-0 top-1/2 -translate-y-1/2 text-white/80 transition-colors hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isPasswordVisible ? (
                    <RiEyeCloseLine size={20} />
                  ) : (
                    <RiEyeLine size={20} />
                  )}
                </button>
              </div>
              <div className="mt-[15px] h-0.5 bg-white" />
            </div>
          )}

          {/* Action Button */}
          <div className="mt-17.5">
            <Button
              title={getButtonTitle()}
              onPress={() => void handleSubmit("button")}
              loading={isLoading}
              disabled={isLoading}
              className="h-10"
            />
          </div>

          {/* Forgotten Password (login mode) */}
          {mode === "login" && (
            <div className="mt-2.5 flex justify-center">
              <TextButton
                title="Forgotten Password?"
                onPress={handleForgotPasswordOpened}
                disabled={isLoading}
              />
            </div>
          )}

          {/* Bottom Toggle */}
          <div className="mt-8 flex items-center justify-center gap-1">
            {mode === "signup" ? (
              <>
                <span className="font-oswald text-sm text-white/60">
                  Already have an account?
                </span>
                <TextButton
                  title="Login"
                  onPress={() => setMode("login")}
                  disabled={isLoading}
                />
              </>
            ) : mode === "login" ? (
              <>
                <span className="font-oswald text-sm text-white/60">
                  Don't have an account?
                </span>
                <TextButton
                  title="Signup"
                  onPress={() => setMode("signup")}
                  disabled={isLoading}
                />
              </>
            ) : (
              <>
                <span className="font-oswald text-sm text-white/60">
                  Remember your password?
                </span>
                <TextButton
                  title="Login"
                  onPress={() => setMode("login")}
                  disabled={isLoading}
                />
              </>
            )}
          </div>
        </div>
      </div>

      <div className="mt-auto w-full pb-8">{footerContent}</div>
    </GradientBackground>
  );
};
